import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import UserTable from './components/UserTable';
import UserFilters from './components/UserFilters';
import BulkActions from './components/BulkActions';
import AddUserModal from './components/AddUserModal';
import UserDetailsModal from './components/UserDetailsModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import * as usersApi from 'shared/api/auth/usersApi';
import { notify } from 'shared/utils/notify';

const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    role: '',
    status: '',
    dateRange: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [selectedUserForDetails, setSelectedUserForDetails] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch users from API
  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      // Сервер: GET /api/auth/users (admin only).
      // Запрашиваем максимально допустимый pageSize (100) и далее
      // делаем фильтрацию/сортировку/пагинацию на клиенте.
      const response = await usersApi.getUsers({
        page: 1,
        pageSize: 100,
        sortBy: 'createdAt',
        sortDirection: 'desc',
      });

      // Поддерживаем оба формата ответа: { items, total, ... } (по спецификации)
      // и просто массив (для совместимости со старыми моками).
      const rawItems = Array.isArray(response)
        ? response
        : Array.isArray(response?.items)
          ? response.items
          : [];

      const transformedUsers = rawItems.map(user => ({
        id: user.userId,
        name: `${user.name || ''} ${user.surname || ''}`.trim() || user.username,
        email: user.email,
        role: mapRole(user.role),
        status: user.isActive
          ? 'active'
          : (user.isVerified ? 'inactive' : 'pending'),
        lastLogin: user.lastLoginAt ? new Date(user.lastLoginAt) : null,
        registrationDate: user.createdAt ? new Date(user.createdAt) : new Date(),
        avatar: user.avatarUrl || null,
        department: user.department || '',
        permissions: getRolePermissions(user.role),
      }));

      setUsers(transformedUsers);
      setFilteredUsers(transformedUsers);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      // Бэкенд может ещё не реализовать endpoint — используем моки в dev-режиме.
      if (err.status === 404 || err.status === 501) {
        loadMockUsers();
      } else if (err.status === 403) {
        setError('Недостаточно прав для просмотра списка пользователей');
      } else {
        setError('Не удалось загрузить список пользователей');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const mapRole = (role) => {
    const roleMap = {
      'admin': 'Администратор',
      'moderator': 'Модератор',
      'manager': 'Менеджер',
      'user': 'Пользователь'
    };
    return roleMap[role?.toLowerCase()] || 'Пользователь';
  };

  const getRolePermissions = (role) => {
    const permMap = {
      'admin': ['admin', 'user_management', 'security_management'],
      'moderator': ['user_management'],
      'manager': ['user_management'],
      'user': []
    };
    return permMap[role?.toLowerCase()] || [];
  };

  const loadMockUsers = () => {
    // Mock users for development when API is not ready
    const mockUsers = [
      {
        id: 1,
        name: "Александр Петров",
        email: "a.petrov@company.ru",
        role: "Администратор",
        status: "active",
        lastLogin: new Date(Date.now() - 3600000),
        registrationDate: new Date(Date.now() - 86400000 * 30),
        avatar: null,
        phone: "+7 (495) 123-45-67",
        department: "IT Отдел",
        permissions: ["admin", "user_management", "security_management"]
      },
      {
        id: 2,
        name: "Мария Иванова",
        email: "m.ivanova@company.ru",
        role: "Менеджер",
        status: "active",
        lastLogin: new Date(Date.now() - 7200000),
        registrationDate: new Date(Date.now() - 86400000 * 25),
        avatar: null,
        phone: "+7 (495) 234-56-78",
        department: "Продажи",
        permissions: ["user_management"]
      }
    ];
    setUsers(mockUsers);
    setFilteredUsers(mockUsers);
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    const filtered = users.filter(user => {
      const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = !filters.role || user.role === filters.role;
      const matchesStatus = !filters.status || user.status === filters.status;

      let matchesDate = true;
      if (filters.dateRange) {
        const now = new Date();
        const userDate = user.registrationDate;
        switch (filters.dateRange) {
          case 'week':
            matchesDate = (now - userDate) <= 7 * 24 * 60 * 60 * 1000;
            break;
          case 'month':
            matchesDate = (now - userDate) <= 30 * 24 * 60 * 60 * 1000;
            break;
          case 'quarter':
            matchesDate = (now - userDate) <= 90 * 24 * 60 * 60 * 1000;
            break;
          default:
            matchesDate = true;
        }
      }

      return matchesSearch && matchesRole && matchesStatus && matchesDate;
    });

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (aValue instanceof Date && bValue instanceof Date) {
          aValue = aValue.getTime();
          bValue = bValue.getTime();
        } else if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [users, searchTerm, filters, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleUserSelect = (userId, isSelected) => {
    setSelectedUsers(prev =>
      isSelected
        ? [...prev, userId]
        : prev.filter(id => id !== userId)
    );
  };

  const handleSelectAll = (isSelected) => {
    const currentPageUsers = getCurrentPageUsers();
    if (isSelected) {
      const newSelections = currentPageUsers.map(user => user.id);
      setSelectedUsers(prev => [...new Set([...prev, ...newSelections])]);
    } else {
      const currentPageUserIds = currentPageUsers.map(user => user.id);
      setSelectedUsers(prev => prev.filter(id => !currentPageUserIds.includes(id)));
    }
  };

  const handleBulkAction = async (action) => {
    const selectedUserData = users.filter(user => selectedUsers.includes(user.id));

    switch (action) {
      case 'activate':
        // Update each user via API
        try {
          await Promise.all(selectedUsers.map(id => 
            usersApi.updateUser(id, { isActive: true })
          ));
          setUsers(prev => prev.map(user =>
            selectedUsers.includes(user.id)
              ? { ...user, status: 'active' }
              : user
          ));
          notify.success(`Активировано пользователей: ${selectedUsers.length}`);
        } catch (err) {
          setError('Не удалось активировать пользователей');
        }
        break;
      case 'deactivate':
        try {
          await Promise.all(selectedUsers.map(id => 
            usersApi.deactivateUser(id)
          ));
          setUsers(prev => prev.map(user =>
            selectedUsers.includes(user.id)
              ? { ...user, status: 'inactive' }
              : user
          ));
          notify.success(`Деактивировано пользователей: ${selectedUsers.length}`);
        } catch (err) {
          setError('Не удалось деактивировать пользователей');
        }
        break;
      case 'delete':
        try {
          await Promise.all(selectedUsers.map(id => 
            usersApi.deactivateUser(id)
          ));
          setUsers(prev => prev.filter(user => !selectedUsers.includes(user.id)));
          notify.success(`Удалено пользователей: ${selectedUsers.length}`);
        } catch (err) {
          setError('Не удалось удалить пользователей');
        }
        break;
      case 'export':
        // Export functionality
        const csvContent = selectedUserData.map(user =>
          `${user.name},${user.email},${user.role},${user.status}`
        ).join('\n');
        console.log('Exported data:', csvContent);
        notify.info('Данные экспортированы');
        break;
    }

    setSelectedUsers([]);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await usersApi.deactivateUser(userId);
      setUsers(prev => prev.filter(user => user.id !== userId));
      setUserToDelete(null);
      notify.success('Пользователь удалён');
    } catch (err) {
      console.error('Failed to delete user:', err);
      setError('Не удалось удалить пользователя');
    }
  };

  const handleAddUser = async (newUser) => {
    try {
      const createdUser = await usersApi.createUserAdmin({
        email: newUser.email,
        username: newUser.email.split('@')[0],
        password: newUser.password || 'TempPassword123!',
        name: newUser.name?.split(' ')[0] || '',
        surname: newUser.name?.split(' ').slice(1).join(' ') || '',
        role: newUser.role?.toLowerCase() || 'user'
      });
      
      const user = {
        id: createdUser.userId,
        name: newUser.name,
        email: newUser.email,
        role: newUser.role,
        status: 'pending',
        registrationDate: new Date(),
        lastLogin: null,
        avatar: null,
        phone: newUser.phone || '',
        department: newUser.department || '',
        permissions: getRolePermissions(newUser.role?.toLowerCase())
      };
      setUsers(prev => [...prev, user]);
      setIsAddUserModalOpen(false);
      notify.success(`Пользователь «${newUser.name || newUser.email}» добавлен`);
    } catch (err) {
      console.error('Failed to create user:', err);
      setError(err.message || 'Не удалось создать пользователя');
    }
  };
  

  const getCurrentPageUsers = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredUsers.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(filteredUsers.length / pageSize);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">Загрузка пользователей...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-md flex items-center justify-between">
            <p className="text-sm text-error">{error}</p>
            <button onClick={() => setError('')} className="text-error hover:text-error-700">
              <Icon name="X" size={16} />
            </button>
          </div>
        )}

        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-text-secondary mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="hover:text-primary transition-colors duration-150"
          >
            Dashboard
          </button>
          <Icon name="ChevronRight" size={16} />
          <span className="text-text-primary font-medium">Управление пользователями</span>
        </nav>

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-text-primary">
              Управление пользователями
            </h1>
            <p className="text-text-secondary mt-1">
              Просмотр и управление всеми пользователями системы
            </p>
          </div>
          <button
            onClick={() => setIsAddUserModalOpen(true)}
            className="
              inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-md
              hover:bg-primary-700 transition-colors duration-150 hover-lift
              focus:outline-none focus:ring-2 focus:ring-primary/20
            "
          >
            <Icon name="UserPlus" size={18} />
            Добавить пользователя
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-surface rounded-lg border border-secondary-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center" style={{color: 'var(--primary)'}}>
                <Icon name="Users" size={20} className="text-primary-600" />
              </div>
              <div>
                <p className="text-sm text-text-secondary">Всего пользователей</p>
                <p className="text-xl font-semibold text-text-primary">{users.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-lg border border-secondary-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-success-100 rounded-lg flex items-center justify-center" style={{color: 'var(--accent)'}}>
                <Icon name="UserCheck" size={20} className="text-success-600" />
              </div>
              <div>
                <p className="text-sm text-text-secondary">Активные</p>
                <p className="text-xl font-semibold text-text-primary">
                  {users.filter(u => u.status === 'active').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-lg border border-secondary-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-warning-100 rounded-lg flex items-center justify-center" style={{color: 'var(--warning)'}}>
                <Icon name="Clock" size={20} className="text-warning-600" />
              </div>
              <div>
                <p className="text-sm text-text-secondary">Ожидают</p>
                <p className="text-xl font-semibold text-text-primary">
                  {users.filter(u => u.status === 'pending').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-lg border border-secondary-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-error-100 rounded-lg flex items-center justify-center" style={{color: 'var(--error)'}}>
                <Icon name="UserX" size={20} className="text-error-600" />
              </div>
              <div>
                <p className="text-sm text-text-secondary">Заблокированы</p>
                <p className="text-xl font-semibold text-text-primary">
                  {users.filter(u => u.status === 'suspended').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-surface rounded-lg border border-secondary-200">
          {/* Toolbar */}
          <div className="p-4 border-b border-secondary-200">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              {/* Search */}
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Icon
                    name="Search"
                    size={16}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400"
                  />
                  <input
                    type="text"
                    placeholder="Поиск по имени или email..."
                    name="usermanager-search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="
                      text-text-primary bg-secondary-100
                      w-full pl-10 pr-4 py-2 border border-secondary-200 rounded-md
                      focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                      transition-all duration-150 text-sm
                    "
                  />
                </div>
              </div>

              {/* Filters */}
              <UserFilters
                filters={filters}
                onFiltersChange={setFilters}
              />
            </div>

            {/* Bulk Actions */}
            {selectedUsers.length > 0 && (
              <div className="mt-4">
                <BulkActions
                  selectedCount={selectedUsers.length}
                  onBulkAction={handleBulkAction}
                />
              </div>
            )}
          </div>

          {/* User Table */}
          <UserTable
            users={getCurrentPageUsers()}
            selectedUsers={selectedUsers}
            sortConfig={sortConfig}
            onSort={handleSort}
            onUserSelect={handleUserSelect}
            onSelectAll={handleSelectAll}
            onViewDetails={setSelectedUserForDetails}
            onDeleteUser={setUserToDelete}
            allSelected={getCurrentPageUsers().length > 0 && getCurrentPageUsers().every(user => selectedUsers.includes(user.id))}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-secondary-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-text-secondary">Показать:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="
                      text-text-primary bg-secondary-50
                      border border-secondary-200 rounded-md px-3 py-1 text-sm
                      focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                    "
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-sm text-text-secondary">
                    из {filteredUsers.length} записей
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="
                      p-2 rounded-md border border-secondary-200 hover:bg-secondary-50
                      disabled:opacity-50 disabled:cursor-not-allowed
                      transition-colors duration-150
                    "
                  >
                    <Icon name="ChevronLeft" size={16} />
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`
                            px-3 py-1 rounded-md text-sm font-medium transition-colors duration-150
                            ${currentPage === page
                              ? 'bg-primary text-white' : 'text-text-secondary hover:bg-secondary-50'
                            }
                          `}
                        >
                          {page}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="
                      p-2 rounded-md border border-secondary-200 hover:bg-secondary-50
                      disabled:opacity-50 disabled:cursor-not-allowed
                      transition-colors duration-150
                    "
                  >
                    <Icon name="ChevronRight" size={16} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {isAddUserModalOpen && (
        <AddUserModal
          onClose={() => setIsAddUserModalOpen(false)}
          onAddUser={handleAddUser}
        />
      )}

      {selectedUserForDetails && (
        <UserDetailsModal
          user={selectedUserForDetails}
          onClose={() => setSelectedUserForDetails(null)}
        />
      )}

      {userToDelete && (
        <DeleteConfirmModal
          user={userToDelete}
          onClose={() => setUserToDelete(null)}
          onConfirm={() => handleDeleteUser(userToDelete.id)}
        />
      )}
    </div>
  );
};

export default UserManagement;